import { nextTick, onMounted, onUnmounted, ref } from 'vue';

export interface VisibilityInfo {
  isVisible: boolean;
  position: 'top' | 'bottom';
  isAboveViewport: boolean;
  isBelowViewport: boolean;
}

export function useErrorVisibility() {
  const elementRef = ref<HTMLElement | undefined>(undefined);
  const isVisible = ref(false);
  const position = ref<'top' | 'bottom'>('bottom');

  const checkVisibility = (): VisibilityInfo => {
    if(!elementRef.value) {
      return {
        isVisible: false,
        position: 'bottom',
        isAboveViewport: false,
        isBelowViewport: false,
      };
    }

    const rect = elementRef.value.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const navbarHeight = 64; // h-16 = 64px
    const footerHeight = 64; // Approximate footer height

    // Check if element is visible in viewport
    const isInViewport = rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= viewportHeight &&
      rect.right <= window.innerWidth;

    // Check if element is above or below viewport
    const isAbove = rect.bottom < navbarHeight;
    const isBelow = rect.top > (viewportHeight - footerHeight);
    // Determine position for floating indicator
    let indicatorPosition: 'top' | 'bottom' = 'bottom';

    if(isAbove) {
      indicatorPosition = 'top';
    } else if(isBelow) {
      indicatorPosition = 'bottom';
    }

    return {
      isVisible: isInViewport && !isAbove && !isBelow,
      position: indicatorPosition,
      isAboveViewport: isAbove,
      isBelowViewport: isBelow,
    };
  };

  const updateVisibility = () => {
    const visibilityInfo = checkVisibility();

    isVisible.value = visibilityInfo.isVisible;
    position.value = visibilityInfo.position;
  };

  // Debounced visibility check
  let visibilityTimeout: number | undefined = undefined;

  const debouncedUpdateVisibility = () => {
    if(visibilityTimeout) {
      clearTimeout(visibilityTimeout);
    }

    visibilityTimeout = window.setTimeout(updateVisibility, 100);
  };

  onMounted(() => {
    // Initial check after next tick to ensure element is rendered
    nextTick(() => {
      updateVisibility();
    });

    // Listen for scroll and resize events
    window.addEventListener('scroll', debouncedUpdateVisibility, { passive: true });
    window.addEventListener('resize', debouncedUpdateVisibility, { passive: true });
  });

  onUnmounted(() => {
    if(visibilityTimeout) {
      clearTimeout(visibilityTimeout);
    }

    window.removeEventListener('scroll', debouncedUpdateVisibility);
    window.removeEventListener('resize', debouncedUpdateVisibility);
  });

  return {
    elementRef,
    isVisible,
    position,
    checkVisibility,
    updateVisibility,
  };
}
